import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'

import {
  Button,
  Card,
  CardBox,
  CardActions,
  CardContent,
  Icons,
  Stack,
  Text,
  useAlert,
  NotificationDialog,
} from '../../components'
import { queries, mutations } from '../../graphql'

export function ManageBankAccounts(props) {
  const { manageBankAccountsRef } = props
  const navigate = useNavigate()
  const { donorFundId } = useOutletContext() ?? {}
  const [open, setOpen] = useState(false)
  const [cannotDeleteOpen, setCannotDeleteOpen] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const bankAccountsResponse = useQuery(queries.paymentMethods.myLinkedBanks, {
    skip: !donorFundId,
    variables: { fundId: donorFundId },
  })
  const myFund = useQuery(queries.funds.myFund)

  const [mutate, { isLoading }] = useMutation(mutations.paymentMethods.deleteBankAccount, {
    onCompleted: () => {
      setAlert({ message: `Removed payment method.`, severity: 'success' })
      setDeletingId(null)
    },
    onError: (newErrors) => {
      setAlert({ message: `An error occured.`, severity: 'error' })
      setDeletingId(null)
    },
    refetchQueries: [{ query: queries.paymentMethods.myLinkedBanks, variables: { fundId: donorFundId } }],
  })

  const bankAccounts = bankAccountsResponse.data?.me?.fund?.linkedBanks
  const hasBankAccounts = bankAccounts && bankAccounts.length

  const handleDelete = () => {
    mutate({
      variables: {
        fundId: donorFundId,
        data: { ids: [deletingId] },
      },
    })
    setOpen(false)
  }

  const handleConfirm = (id) => {
    setOpen(true)
    setDeletingId(id)
  }
  const handleDialogClose = () => {
    setOpen(false)
    setCannotDeleteOpen(false)
    setDeletingId(null)
  }

  return (
    <Stack ref={manageBankAccountsRef}>
      <CardBox>
        <Stack direction="row">
          <Text.H4>Bank Accounts</Text.H4>
        </Stack>
        <br />
        <Stack direction="row" flexWrap="wrap" justifyContent="flex-start">
          {hasBankAccounts ? (
            bankAccounts.map((ba) => {
              const srcObj = ba.details

              return (
                <Card
                  key={ba.id}
                  variant="outlined"
                  style={{
                    minWidth: 300,
                    maxWidth: 300,
                    margin: 10,
                    backgroundColor: 'lightblue',
                    borderColor: 'lightblue',
                  }}
                >
                  <CardContent>
                    <Text.H5>{`${srcObj.institution}`}</Text.H5>
                    <br />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Text.Body>{`••••••••${srcObj.last4}`}</Text.Body>
                    </Stack>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      color="error"
                      variant="text"
                      onClick={ba.canDelete ? () => handleConfirm(ba.id) : () => setCannotDeleteOpen(true)}
                    >
                      <Icons.DeleteForever fontSize="small" />
                      Remove
                    </Button>
                  </CardActions>
                </Card>
              )
            })
          ) : (
            <Text.Body>
              No saved bank accounts.{' '}
              <Link to={`/funds/${myFund.data?.me?.fund.id}/contribute/bank-transfer`}>Add Funds</Link> to save a bank
              account.
            </Text.Body>
          )}
        </Stack>
        <br />
        <NotificationDialog
          open={cannotDeleteOpen}
          onClose={handleDialogClose}
          type="alert"
          message={
            <>
              This bank account cannot be removed because it is linked to a scheduled or recurring contribution.
              <br />
              <br />
              To remove this payment method, please first cancel any scheduled or recurring contributions.
            </>
          }
          actions={[
            { label: 'Close', onClick: handleDialogClose },
            {
              label: 'Go to my activity',
              onClick: () => navigate(`/funds/${myFund.data?.me?.fund.id}/activity/giving-wallet`),
              autoFocus: true,
            },
          ]}
        />
        <NotificationDialog
          open={open}
          onClose={handleDialogClose}
          type="alert"
          title="Remove Bank Account"
          message="Are you sure you want to remove this bank account from your profile?"
          actions={[
            { label: 'Cancel', onClick: handleDialogClose },
            { label: 'Yes, remove it', onClick: handleDelete, autoFocus: true },
          ]}
        />
        <Alert {...alertProps} />
      </CardBox>
    </Stack>
  )
}
